import type { WebhookDeliveryView, WebhookEndpointView } from "@developerz.ai/ggcoin-sdk";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { formatDateTime } from "../lib/format.ts";

type OutcomeFilter = "all" | "delivered" | "retryable" | "permanent";
const OUTCOMES: OutcomeFilter[] = ["all", "delivered", "retryable", "permanent"];

/**
 * Webhook delivery log (#25): every outbound attempt — success, retry, or
 * permanent drop — newest-first, with a Refresh and an outcome filter. The
 * endpoint id is resolved to its label via the endpoints list for readability.
 */
export function Deliveries() {
  const { client } = useAuth();
  const [rows, setRows] = createSignal<WebhookDeliveryView[]>([]);
  const [endpoints, setEndpoints] = createSignal<WebhookEndpointView[]>([]);
  const [nextBefore, setNextBefore] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);
  const [outcome, setOutcome] = createSignal<OutcomeFilter>("all");

  async function load(before?: string) {
    setLoading(true);
    setError(null);
    try {
      const [page, eps] = await Promise.all([
        client.listWebhookDeliveries(before ? { before } : undefined),
        before ? Promise.resolve(null) : client.listWebhookEndpoints(),
      ]);
      if (eps) setEndpoints(eps);
      setRows((prev) => (before ? [...prev, ...page.deliveries] : page.deliveries));
      setNextBefore(page.nextBefore);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
  });

  const labelOf = createMemo(() => {
    const byId = new Map(endpoints().map((e) => [e.id, e.label]));
    return (id: string) => byId.get(id) ?? `${id.slice(0, 8)}…`;
  });

  const visible = createMemo(() =>
    outcome() === "all" ? rows() : rows().filter((r) => r.outcome === outcome()),
  );

  return (
    <section>
      <div class="row" style={{ "align-items": "center", "justify-content": "space-between" }}>
        <h1 style={{ margin: 0 }}>Webhook deliveries</h1>
        <button type="button" class="ghost" disabled={loading()} onClick={() => load()}>
          {loading() ? "…" : "Refresh"}
        </button>
      </div>
      <p class="muted">
        Every outbound delivery attempt, newest first. Retried events show one row per attempt.
      </p>
      <ErrorBanner error={error()} />

      <div class="row" style={{ gap: "0.5rem", "align-items": "center", margin: "0.5rem 0" }}>
        <label for="outcome-filter" class="muted">
          Outcome
        </label>
        <select
          id="outcome-filter"
          value={outcome()}
          onChange={(e) => setOutcome(e.currentTarget.value as OutcomeFilter)}
        >
          <For each={OUTCOMES}>
            {(o) => <option value={o}>{o === "all" ? "All outcomes" : o}</option>}
          </For>
        </select>
      </div>

      <div class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Event</th>
              <th>Endpoint</th>
              <th>Attempt</th>
              <th>Outcome</th>
              <th>HTTP</th>
              <th>Duration</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={visible().length > 0}
              fallback={
                <tr>
                  <td colSpan={8} class="muted">
                    {loading()
                      ? "Loading…"
                      : rows().length === 0
                        ? "No deliveries yet."
                        : "No deliveries match this filter."}
                  </td>
                </tr>
              }
            >
              <For each={visible()}>
                {(d) => (
                  <tr>
                    <td>{formatDateTime(d.createdAt)}</td>
                    <td>{d.eventType}</td>
                    <td>{labelOf()(d.endpointId)}</td>
                    <td class="num">{d.attempt}</td>
                    <td>
                      <span class={`badge badge-${badgeFor(d.outcome)}`}>{d.outcome}</span>
                    </td>
                    <td class="num">{d.statusCode ?? "—"}</td>
                    <td class="num">{d.durationMs}ms</td>
                    <td class="url-cell">
                      <Show when={d.error} fallback={"—"}>
                        <code>{d.error}</code>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      <Show when={nextBefore()}>
        <div class="row" style={{ "margin-top": "0.75rem" }}>
          <button
            type="button"
            class="ghost"
            disabled={loading()}
            onClick={() => load(nextBefore() ?? undefined)}
          >
            {loading() ? "Loading…" : "Load older"}
          </button>
        </div>
      </Show>
    </section>
  );
}

/** Map a delivery outcome to an existing status-badge variant. */
function badgeFor(outcome: WebhookDeliveryView["outcome"]): "active" | "failed" | "pending" {
  if (outcome === "delivered") return "active";
  if (outcome === "permanent") return "failed";
  return "pending"; // retryable
}
